import { graphql } from "gql.tada";
import { useMutation, useQuery, useSubscription } from "@apollo/client/react";

const VALIDATE_STEP_RULE_SET_MUTATION = graphql(`
  mutation ValidateStepRuleSet($stepRuleSetId: ID!) {
    validateStepRuleSet(stepRuleSetId: $stepRuleSetId) {
      id
      stepRuleSetId
      status
      stage
      errorSummary
      startedAt
      completedAt
    }
  }
`);

const CANCEL_VALIDATION_MUTATION = graphql(`
  mutation CancelStepRuleSetValidation($jobId: ID!) {
    cancelStepRuleSetValidation(jobId: $jobId) {
      id
      status
      completedAt
    }
  }
`);

const VALIDATION_PROGRESS_SUBSCRIPTION = graphql(`
  subscription ValidationProgress($jobId: ID!) {
    validationProgress(jobId: $jobId) {
      id
      status
      stage
      progress {
        percent
        currentPhaseDetail
        rulesFired
        totalRulesDeclared
        totalMutations
        elapsedMs
      }
      errorSummary
      completedAt
    }
  }
`);

const VALIDATION_EVENTS_SUBSCRIPTION = graphql(`
  subscription ValidationEvents($jobId: ID!) {
    validationEvents(jobId: $jobId) {
      id
      sequence
      timestamp
      kind
      summary
      ruleId
    }
  }
`);

function useValidateStepRuleSet() {
  return useMutation(VALIDATE_STEP_RULE_SET_MUTATION);
}

function useCancelValidation() {
  return useMutation(CANCEL_VALIDATION_MUTATION);
}

function useValidationProgress(jobId: string | null) {
  return useSubscription(VALIDATION_PROGRESS_SUBSCRIPTION, {
    variables: { jobId: jobId ?? "" },
    skip: !jobId,
  });
}

function useValidationEvents(jobId: string | null) {
  return useSubscription(VALIDATION_EVENTS_SUBSCRIPTION, {
    variables: { jobId: jobId ?? "" },
    skip: !jobId,
  });
}

const LATEST_TERMINAL_VALIDATION_JOB_QUERY = graphql(`
  query LatestTerminalValidationJob($stepRuleSetId: ID!) {
    stepRuleSetValidationJobs(
      stepRuleSetId: $stepRuleSetId
      page: { first: 5 }
    ) {
      nodes {
        id
        status
      }
    }
  }
`);

const RULE_FIRED_EVENTS_QUERY = graphql(`
  query RuleFiredEvents($jobId: ID!) {
    stepRuleSetValidationEvents(
      jobId: $jobId
      kindFilter: [RULE_FIRED]
      page: { first: 500 }
    ) {
      nodes {
        id
        ruleId
      }
    }
  }
`);

/**
 * Returns a map of ruleId -> fire count from the latest terminal validation
 * job for the given step rule set. Returns null if no terminal job exists.
 */
function useRuleFireCounts(
  stepRuleSetId: string | null,
): Map<string, number> | null {
  const { data: jobsData } = useQuery(LATEST_TERMINAL_VALIDATION_JOB_QUERY, {
    variables: { stepRuleSetId: stepRuleSetId ?? "" },
    skip: !stepRuleSetId,
  });

  const terminalJob = (jobsData?.stepRuleSetValidationJobs?.nodes ?? []).find(
    (j) => isTerminalStatus(toValidationJobStatus(j.status)),
  );

  const { data: eventsData } = useQuery(RULE_FIRED_EVENTS_QUERY, {
    variables: { jobId: terminalJob?.id ?? "" },
    skip: !terminalJob?.id,
  });

  if (!terminalJob) return null;

  const counts = new Map<string, number>();
  for (const event of eventsData?.stepRuleSetValidationEvents?.nodes ?? []) {
    if (event.ruleId) {
      counts.set(event.ruleId, (counts.get(event.ruleId) ?? 0) + 1);
    }
  }
  return counts;
}

// ---------------------------------------------------------------------------
// Shared invariant types & narrowing helpers
// ---------------------------------------------------------------------------

/**
 * All possible statuses for a rule-set validation job.
 * Used as a branded string union so components can use Record<> lookups
 * without casting from `string`.
 */
type ValidationJobStatus =
  | "QUEUED"
  | "RUNNING"
  | "SUCCEEDED"
  | "FAILED"
  | "CANCELLED";

/**
 * Ordered stages of a validation run.
 */
type ValidationStage =
  | "QUEUED"
  | "SEEDING"
  | "UPSTREAM_RUNNING"
  | "GENERATING"
  | "PLAYING"
  | "ASSERTING"
  | "FINALISING";

/**
 * Kinds of validation events emitted by the backend. Matches the backend
 * `ValidationEventKind` enum.
 */
type ValidationEventKind =
  | "RULE_FIRED"
  | "MATCH_UPDATED"
  | "EFFECT_APPLIED"
  | "SCOPE_ENDED"
  | "CROSS_STEP_EVALUATED"
  | "ERROR"
  | "ASSERTION";

const VALIDATION_JOB_STATUSES: readonly ValidationJobStatus[] = [
  "QUEUED",
  "RUNNING",
  "SUCCEEDED",
  "FAILED",
  "CANCELLED",
] as const;

const VALIDATION_STAGES: readonly ValidationStage[] = [
  "QUEUED",
  "SEEDING",
  "UPSTREAM_RUNNING",
  "GENERATING",
  "PLAYING",
  "ASSERTING",
  "FINALISING",
] as const;

const VALIDATION_EVENT_KINDS: readonly ValidationEventKind[] = [
  "RULE_FIRED",
  "MATCH_UPDATED",
  "EFFECT_APPLIED",
  "SCOPE_ENDED",
  "CROSS_STEP_EVALUATED",
  "ERROR",
  "ASSERTION",
] as const;

/** Narrows an unknown string coming from the API to a ValidationJobStatus. */
function toValidationJobStatus(value: string): ValidationJobStatus {
  return (VALIDATION_JOB_STATUSES as readonly string[]).includes(value)
    ? (value as ValidationJobStatus)
    : "QUEUED";
}

/** Narrows an unknown string coming from the API to a ValidationStage. */
function toValidationStage(value: string): ValidationStage {
  return (VALIDATION_STAGES as readonly string[]).includes(value)
    ? (value as ValidationStage)
    : "QUEUED";
}

/** Narrows an unknown string coming from the API to a ValidationEventKind. */
function toValidationEventKind(value: string): ValidationEventKind | null {
  return (VALIDATION_EVENT_KINDS as readonly string[]).includes(value)
    ? (value as ValidationEventKind)
    : null;
}

/**
 * Narrows an unknown scalar value (DateTime maps to `unknown` in gql-tada's
 * default setupSchema) to a safe ISO string. Non-string inputs fall back to
 * the current time so we never surface `"null"` or `"undefined"` to the UI.
 */
function toIsoTimestamp(value: unknown): string {
  if (typeof value === "string") return value;
  if (value instanceof Date) return value.toISOString();
  return new Date().toISOString();
}

/** Narrows an unknown scalar value to a string or null (for optional fields). */
function toOptionalIsoTimestamp(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return value;
  if (value instanceof Date) return value.toISOString();
  return null;
}

/** Returns true when the status represents a finished (non-active) run. */
function isTerminalStatus(status: ValidationJobStatus): boolean {
  return (
    status === "SUCCEEDED" || status === "FAILED" || status === "CANCELLED"
  );
}

/** Returns true when the status represents an in-flight run. */
function isActiveStatus(status: ValidationJobStatus): boolean {
  return status === "QUEUED" || status === "RUNNING";
}

/** Maps each job status to its Mantine colour token. */
const VALIDATION_STATUS_COLORS: Record<ValidationJobStatus, string> = {
  QUEUED: "blue",
  RUNNING: "blue",
  SUCCEEDED: "green",
  FAILED: "red",
  CANCELLED: "gray",
};

/** Maps each validation stage to a human-readable label. */
const VALIDATION_STAGE_LABELS: Record<ValidationStage, string> = {
  QUEUED: "Queued",
  SEEDING: "Seeding participants",
  UPSTREAM_RUNNING: "Running upstream steps",
  GENERATING: "Generating structure",
  PLAYING: "Simulating matches",
  ASSERTING: "Asserting invariants",
  FINALISING: "Finalising",
};

/** Mantine colour tokens used to decorate event-kind labels. */
const VALIDATION_EVENT_KIND_COLORS: Record<ValidationEventKind, string> = {
  RULE_FIRED: "var(--mantine-color-blue-6)",
  MATCH_UPDATED: "var(--mantine-color-green-6)",
  EFFECT_APPLIED: "var(--mantine-color-teal-6)",
  SCOPE_ENDED: "var(--mantine-color-gray-6)",
  CROSS_STEP_EVALUATED: "var(--mantine-color-violet-6)",
  ERROR: "var(--mantine-color-red-6)",
  ASSERTION: "var(--mantine-color-yellow-7)",
};

export {
  VALIDATE_STEP_RULE_SET_MUTATION,
  CANCEL_VALIDATION_MUTATION,
  VALIDATION_PROGRESS_SUBSCRIPTION,
  VALIDATION_EVENTS_SUBSCRIPTION,
  LATEST_TERMINAL_VALIDATION_JOB_QUERY,
  RULE_FIRED_EVENTS_QUERY,
  useValidateStepRuleSet,
  useCancelValidation,
  useValidationProgress,
  useValidationEvents,
  useRuleFireCounts,
  VALIDATION_STATUS_COLORS,
  VALIDATION_STAGE_LABELS,
  VALIDATION_EVENT_KIND_COLORS,
  VALIDATION_EVENT_KINDS,
  toValidationJobStatus,
  toValidationStage,
  toValidationEventKind,
  toIsoTimestamp,
  toOptionalIsoTimestamp,
  isTerminalStatus,
  isActiveStatus,
};
export type { ValidationJobStatus, ValidationStage, ValidationEventKind };
